import { CircleCheck, Clock, SquareArrowOutUpRight } from "lucide-react";
import React, { FC, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IgButton, IgChip, IgLink, IgTypography } from "@intelligems/ig-theme";
import { LivePriceDto, OnboardingInfoFe } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { getMixpanelStepTitle } from "@src/components/MainApp/Onboarding/utils";
import { useViewPage } from "@src/hooks/mixpanel";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { setDialog } from "@src/store/slices/global";
import { theme } from "@src/theme";

interface OnboardingBillingPlanProps {
  data: Pick<
    OnboardingInfoFe,
    | "preferredName"
    | "goals"
    | "userReportedOrderCount"
    | "recommendedPlan"
    | "recommendedLivePlan"
    | "storeType"
  >;
  priceData: LivePriceDto[];
  orgOrderCount: number;
  onClose: () => void;
  onChange: (
    field: keyof OnboardingInfoFe,
    value: string | LivePriceDto
  ) => void;
}

const isCoreOrPlus = (
  data: Pick<
    OnboardingInfoFe,
    "goals" | "userReportedOrderCount" | "storeType"
  >,
  orgOrderCount: number,
  priceData: LivePriceDto[]
) => {
  const annualPlus = priceData.find(
    (pData) => pData.interval === "year" && pData.product.name === "Plus"
  );
  const monthlyPlus = priceData.find(
    (pData) => pData.interval === "month" && pData.product.name === "Plus"
  );
  const annualCore = priceData.find(
    (pData) => pData.interval === "year" && pData.product.name === "Core"
  );
  const monthlyCore = priceData.find(
    (pData) => pData.interval === "month" && pData.product.name === "Core"
  );
  const isHeadless = data.storeType === "headless";
  if (
    data.userReportedOrderCount === "500-2,000 orders" ||
    data.userReportedOrderCount === "<500 orders" ||
    (data.userReportedOrderCount === "I don't know" && orgOrderCount < 2000)
  ) {
    if (
      data.goals.includes("Optimize Product Pricing") ||
      data.goals.includes("Tweak Shipping Strategy")
    ) {
      return isHeadless
        ? annualPlus || monthlyPlus || priceData[0]
        : monthlyPlus || annualPlus || priceData[0];
    } else {
      return isHeadless
        ? annualCore || monthlyCore || priceData[0]
        : monthlyCore || annualCore || priceData[0];
    }
  } else {
    if (
      data.goals.includes("Boost Profit Margins") ||
      data.goals.includes("Optimize Product Pricing") ||
      data.goals.includes("Tweak Shipping Strategy")
    ) {
      return isHeadless
        ? annualPlus || monthlyPlus || priceData[0]
        : monthlyPlus || annualPlus || priceData[0];
    } else {
      return isHeadless
        ? annualCore || monthlyCore || priceData[0]
        : monthlyCore || annualCore || priceData[0];
    }
  }
};

export const OnboardingBillingPlan = ({
  data,
  priceData,
  orgOrderCount,
  onClose,
  onChange,
}: OnboardingBillingPlanProps) => {
  const theme = useTheme();
  const calculatedPlan = isCoreOrPlus(data, orgOrderCount, priceData);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const calculatedPlanFeatures = useMemo(() => {
    const selectedPlanFeatures = priceData.filter(
      (data) => data.product.name === calculatedPlan.product.name
    )[0]?.features;
    if (calculatedPlan.product.name === "Core") {
      return selectedPlanFeatures;
    } else {
      const corePlanFeatures = priceData.find(
        (data) => data.product.name === "Core"
      )?.features;
      if (corePlanFeatures) {
        return [
          ...corePlanFeatures,
          ...(selectedPlanFeatures || []).filter(
            (feat) => feat !== "Everything in Core"
          ),
        ];
      }
      return selectedPlanFeatures;
    }
  }, [calculatedPlan, priceData]);
  useEffect(() => {
    if (!data.recommendedPlan) {
      onChange("recommendedPlan", calculatedPlan.product.name);
    }
    if (!data.recommendedLivePlan) {
      onChange("recommendedLivePlan", calculatedPlan);
    }
  }, [data.recommendedPlan, calculatedPlan]);
  useViewPage(`Onboarding - ${getMixpanelStepTitle(4)}`);
  return (
    <Grid
      sx={{
        ".MuiBackdrop-root": {
          backgroundColor: "transparent !important",
        },
      }}
      container
      item
      justifyContent={"center"}
      direction={"column"}
      rowSpacing={4}
      width={"100%"}
    >
      <Grid item container justifyContent={"center"}>
        <Grid
          item
          container
          justifyContent={"center"}
          alignItems={"center"}
          sx={{
            backgroundColor: theme.igBackgroundColors.accent,
            width: "64px",
            height: "64px",
            borderRadius: "50%",
          }}
        >
          <CircleCheck size={32} color={theme.igIconColors.accent} />
        </Grid>
      </Grid>
      <Grid item container justifyContent={"center"} rowSpacing={2} mb={6}>
        <Grid item>
          <IgTypography variant={"h1"} fontWeight={"600"}>
            {data.preferredName.split(" ")[0]}, you're ready to get started!
          </IgTypography>
        </Grid>
        <Grid item>
          <IgTypography>
            We've activated your{" "}
            {calculatedPlan.product.name === "Core" ? "14" : "7"}-day trial with
            our recommended plan to help you achieve your goals faster.
          </IgTypography>
        </Grid>
      </Grid>
      <Grid
        item
        sx={{
          border: `2px solid ${theme.igBorders.accent.default}`,
          borderRadius: "12px",
          padding: 6,
        }}
      >
        <Grid rowSpacing={3} item container direction={"column"}>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"space-between"}
          >
            <Grid item>
              <IgTypography
                variant={"h2"}
                color={theme.palette.text.accent}
                fontWeight={"600"}
              >
                {calculatedPlan.product.name}
              </IgTypography>
            </Grid>
            <Grid item>
              <IgChip
                color={"success"}
                size={"small"}
                label={
                  <IgTypography color={theme.palette.text.tonal.success}>
                    Recommended
                  </IgTypography>
                }
              />
            </Grid>
          </Grid>

          <Grid item>
            <IgTypography>
              {calculatedPlan.product.name === "Core"
                ? "For stores looking to run consistent A/B tests and build personalized experiences that go beyond traditional CRO."
                : "For stores that want to take big swings towards margin-focused outcomes."}
            </IgTypography>
          </Grid>

          <Grid item container direction={"column"} rowSpacing={1} mt={2}>
            {calculatedPlanFeatures.map((feat, index) => (
              <Grid
                columnSpacing={2}
                item
                container
                direction={"row"}
                key={`feat-${index}`}
              >
                <Grid item>
                  <CircleCheck size={18} color={theme.igIconColors.accent} />
                </Grid>
                <Grid item>
                  <IgTypography>{feat}</IgTypography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        container
        mt={6}
        direction={"row"}
        wrap={"nowrap"}
        padding={4}
        alignItems={"flex-start"}
        sx={{
          backgroundColor: theme.igBackgroundColors.accent,
          borderRadius: "8px",
        }}
      >
        <Grid item>
          <IgTypography color={theme.palette.text.accent}>
            Based on your goals and experience level, we recommend starting with
            our {calculatedPlan.product.name} plan to build a strong foundation
            for your optimization strategy.
          </IgTypography>
        </Grid>
        <Grid item container justifyContent={"flex-end"} xs={7}>
          <IgButton
            sx={{
              color: theme.palette.text.accent,
              "&:hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.interactive.accent.hover,
              },
            }}
            color={"link"}
            endIcon={
              <SquareArrowOutUpRight
                color={theme.igIconColors.accent}
                size={16}
              />
            }
            onClick={() => {
              onClose();
              void navigate(SETTINGS_ROUTES.Settings, {
                state: { targetId: "intelligems-plan" },
              });
              dispatch(setDialog({ open: false, dialog: "onboarding" }));
            }}
            variant={"text"}
          >
            Compare All Plans
          </IgButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
